/**
 * What the ground is under a point: trenches, roads, ponds and the water of a barrier. Split out of
 * `statics.ts` for the file limit; `statics.ts` re-exports every name, so nothing else changed.
 */

import { barrierWaterAt } from './hazards/terrain';
import type { MissionDef } from './types';

function distanceToPolyline(points: ReadonlyArray<readonly [number, number]>, x: number, z: number): number {
  let best = Infinity;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    const d2 = dx * dx + dz * dz;
    if (d2 < best) best = d2;
  }
  return Math.sqrt(best);
}

/** Distance from the centre line of the nearest trench, as a fraction of its half-width (0 = centre). */
export function trenchDepthAt(def: MissionDef, x: number, z: number): number {
  let depth = 0;
  for (const t of def.trenches) {
    const d = distanceToPolyline(t.points, x, z);
    const half = t.width / 2;
    if (d < half + 0.5) {
      // Steep sides: full depth inside, ramping to zero over half a metre outside.
      const k = d <= half ? 1 : 1 - (d - half) / 0.5;
      depth = Math.max(depth, t.depth * k);
    }
  }
  return depth;
}

export function inTrench(def: MissionDef, x: number, z: number): boolean {
  for (const t of def.trenches) {
    if (distanceToPolyline(t.points, x, z) <= t.width / 2) return true;
  }
  return false;
}

export function roadDistance(def: MissionDef, x: number, z: number): number {
  let best = Infinity;
  for (const r of def.roads) best = Math.min(best, distanceToPolyline(r.points, x, z) - r.width / 2);
  return best;
}

export function pondDepthAt(def: MissionDef, x: number, z: number): number {
  let depth = 0;
  for (const p of def.ponds) {
    const dx = x - p.x;
    const dz = (z - p.z) * 1.2;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < p.r) depth = Math.max(depth, 1.3 * Math.min(1, (p.r - d) / (p.r * 0.55)));
  }
  return depth;
}

export function inPond(def: MissionDef, x: number, z: number): boolean {
  return pondDepthAt(def, x, z) > 0.35;
}

/* HAZ: water barriers (mission 6's river). The geometry lives in hazards/terrain.ts. */
export { iceAt, weakIceAt } from './hazards/terrain';

/** Water underfoot, to wade or to hide in: a pond, or a barrier's open water. Ice is not water. */
export function waterAt(def: MissionDef, x: number, z: number): boolean {
  return inPond(def, x, z) || barrierWaterAt(def, x, z);
}

